import { useQuery } from 'react-query';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import ApiService from '@jwp/ott-common/src/services/ApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { createURL } from '@jwp/ott-common/src/utils/urlFormatting';
import { type AdConfig } from 'packages/common/types/ad-schedule';
import { type PlaylistItem } from 'packages/common/types/playlist';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';

const CACHE_TIME = 60 * 1000 * 20;

/**
 * @deprecated Use adScheduleUrls.xml form the config instead.
 */
const useLegacyStandaloneAds = ({ adScheduleId, enabled }: { adScheduleId: string | null | undefined; enabled: boolean }) => {
  const apiService = getModule(ApiService);

  const { isLoading, data } = useQuery(['ad-schedule', adScheduleId], async () => apiService.getAdSchedule(adScheduleId), {
    enabled: enabled && !!adScheduleId,
    cacheTime: CACHE_TIME,
    staleTime: CACHE_TIME,
  });

  return {
    isLoading,
    data,
  };
};

export const useAds = ({ mediaId, item }: { mediaId: string; item: PlaylistItem }) => {
  const { adSchedule: adScheduleId, adConfig: adConfigId, adScheduleUrls, adDeliveryMethod: JWPlayerAdDeliveryMethod } = useConfigStore((s) => s.config);
  const { adConfigLevel, featuredAdDeliveryMethod } = useAppConfig();

  // We use client side ads only when delivery method is not pointing at server ads
  // adConfig and adScheduled can't be enabled at the same time
  const adDeliveryMethod = featuredAdDeliveryMethod || JWPlayerAdDeliveryMethod;
  
  const useAdConfigFlow =
    !!adConfigId &&
    adDeliveryMethod !== 'ssai' &&
    (item.is_ssai_enabled ?? 'false') !== 'true' &&
    adConfigLevel !== 'none' &&
    adConfigLevel != null &&
    adConfigLevel != undefined &&
    adConfigLevel != '';

  const { data: adSchedule, isLoading: isAdScheduleLoading } = useLegacyStandaloneAds({ adScheduleId, enabled: !!adScheduleId });

  let updatedUrl: string;
  if (item?.adConfig && adScheduleUrls?.xml) {
    updatedUrl = adScheduleUrls?.xml.replace(/ad_configs\/[^/]+\//, `ad_configs/${item.adConfig}/`);
  }

  const getAdData = () => {
    if (adConfigLevel === 'global') {
      return {
        client: 'vast',
        schedule: createURL(adScheduleUrls?.xml || '', {
          media_id: mediaId,
        }),
      } as AdConfig;
    } else if (adConfigLevel === 'media') {
      return {
        client: 'vast',
        schedule: createURL(updatedUrl || '', {
          media_id: mediaId,
        }),
      } as AdConfig;
    } else if (adConfigLevel === 'globalandmedia') {
      return item?.adConfig
        ? ({
            client: 'vast',
            schedule: createURL(updatedUrl || '', {
              media_id: mediaId,
            }),
          } as AdConfig)
        : ({
            client: 'vast',
            schedule: createURL(adScheduleUrls?.xml || '', {
              media_id: mediaId,
            }),
          } as AdConfig);
    }
  };

  const adConfig = useAdConfigFlow ? getAdData() : undefined;

  return {
    isLoading: useAdConfigFlow ? false : isAdScheduleLoading,
    data: useAdConfigFlow ? adConfig : adSchedule,
  };
};
